// src/modules/app-views/omnichannel/pages/Init/modals/OmnichannelParametersModal/tabs/WhatsappUnifiedTab.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useEffect, useMemo } from 'react';
import { Button, Flex, Popconfirm, Space, Switch, Table, Tag, Tooltip } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import translateX from 'utils/translateX';
import { parsePhoneToDisplay } from 'utils/helpers';
import appService from 'services/appService';
import omnichannelService from 'modules/app-views/omnichannel/service';
import { subscribe as sseSubscribe } from 'modules/app-views/omnichannel/pages/Init/components/omnichannelSseManager';
import AddWhatsappModal from '../modals/AddWhatsappModal';
import EditMetaPhoneModal from '../modals/EditMetaPhoneModal';
import ReconnectBaileysModal from '../modals/ReconnectBaileysModal';
import WhatsAppSourceTag from '../../../components/WhatsAppSourceTag';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const WhatsAppTab = ({ loading, setLoading }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [numbers, setNumbers] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [showEditMetaModal, setShowEditMetaModal] = useState(false);
  const [showReconnectModal, setShowReconnectModal] = useState(false);
  const [reconnectRecord, setReconnectRecord] = useState(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchNumbers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Subscribe to real-time source status changes via the shared SSE manager.
  // Handles phone disconnection (from the device side) and source removal without
  // requiring the user to close and reopen the modal.
  useEffect(() => {
    const unsub = sseSubscribe('omnichannel:source-status-changed', ({ sourceId, connectionStatus }) => {
      if (!sourceId || !connectionStatus) return;
      if (connectionStatus === 'removed') {
        // Source was deleted externally — remove from list immediately
        setNumbers(prev => prev.filter(n => n._id !== sourceId));
      } else {
        setNumbers(prev => {
          // If source is not in list yet, handleSaveSuccess will refresh when added via modal
          if (!prev.some(n => n._id === sourceId)) return prev;
          return prev.map(n => n._id === sourceId ? { ...n, connectionStatus } : n);
        });
      }
    });
    return unsub;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const fetchNumbers = useCallback(async () => {
    setLoading(true);
    try {
      const [metaPhones, baileysInstances] = await Promise.all([
        omnichannelService
          .getWhatsappPhones()
          .then(([d]) => (d || []).map(p => ({ ...p, sourceType: 'meta' }))),
        omnichannelService
          .getBaileysInstances()
          .then(([d]) => (d || []).map(p => ({ ...p, sourceType: 'baileys' }))),
      ]);
      setNumbers([...metaPhones, ...baileysInstances]);
    } catch (error) {
      console.error('[WhatsappUnifiedTab] fetchNumbers error:', error);
      appService.message({ flag: 'e', text: 'error_loading' });
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const handleAdd = useCallback(() => {
    setEditingRecord(null);
    setShowAddModal(true);
  }, []);

  const handleEdit = useCallback(record => {
    setEditingRecord(record);
    setShowEditMetaModal(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setEditingRecord(null);
  }, []);

  const handleSaveSuccess = useCallback(async () => {
    await fetchNumbers();
    // Notify Conversation.jsx to re-evaluate isChannelRemoved / isChannelDisconnected
    window.dispatchEvent(new CustomEvent('omnichannel:sources-refresh'));
  }, [fetchNumbers]);

  const handleDelete = useCallback(
    async record => {
      setLoading(true);
      try {
        if (record.sourceType === 'meta') {
          await omnichannelService.removeWhatsappPhone(record._id);
        } else {
          await omnichannelService.removeBaileysInstance(record._id);
        }
        appService.message({ flag: 's', text: 'removed_successfully' });
        await fetchNumbers();
        // Notify Conversation.jsx so isChannelRemoved activates immediately
        window.dispatchEvent(new CustomEvent('omnichannel:sources-refresh'));
      } catch (error) {
        console.error('[WhatsappUnifiedTab] handleDelete error:', error);
        appService.message({ flag: 'e', text: 'error_removing' });
      } finally {
        setLoading(false);
      }
    },
    [setLoading, fetchNumbers]
  );

  const handleToggleActive = useCallback(
    async (record, checked) => {
      setLoading(true);
      try {
        if (record.sourceType === 'meta') {
          await omnichannelService.updateWhatsappPhone(record._id, { isActive: checked });
        } else {
          await omnichannelService.updateBaileysInstance(record._id, { isActive: checked });
        }
        setNumbers(prev => prev.map(p => (p._id === record._id ? { ...p, isActive: checked } : p)));
      } catch (error) {
        console.error('[WhatsappUnifiedTab] handleToggleActive error:', error);
        appService.message({ flag: 'e', text: 'error_saving' });
      } finally {
        setLoading(false);
      }
    },
    [setLoading]
  );

  const handleReconnect = useCallback(record => {
    setReconnectRecord(record);
    setShowReconnectModal(true);
  }, []);

  const handleReconnectSuccess = useCallback(async () => {
    await fetchNumbers();
    // Notify Conversation.jsx to refresh its sources state so isChannelDisconnected updates
    window.dispatchEvent(new CustomEvent('omnichannel:sources-refresh'));
  }, [fetchNumbers]);

  const handleReconnectAfterClose = useCallback(() => {
    setReconnectRecord(null);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const columns = useMemo(
    () => [
      {
        title: translateX('title'),
        dataIndex: ['displayName'],
        render: text => <span style={{ fontWeight: 500 }}>{text || '—'}</span>,
      },
      {
        title: translateX('phone'),
        dataIndex: ['phoneNumber'],
        width: 280,
        render: text => (
          <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{formatPhone(text) || '—'}</span>
        ),
      },
      {
        title: translateX('origin'),
        dataIndex: ['sourceType'],
        width: 120,
        render: sourceType => <WhatsAppSourceTag sourceType={sourceType} />,
      },
      {
        title: translateX('connection'),
        dataIndex: ['connectionStatus'],
        width: 130,
        render: (connectionStatus, record) => {
          // Meta phones use API tokens (no real-time connectionStatus like Baileys).
          // Show connected if isActive and phoneNumberId is configured.
          if (record.sourceType !== 'baileys') {
            if (record.isActive && record.phoneNumberId) {
              return <Tag color="success">{translateX('connected')}</Tag>;
            }
            return <Tag color="default">{translateX('inactive')}</Tag>;
          }
          return <ConnectionStatusTag status={connectionStatus} />;
        },
      },
      {
        title: translateX('status'),
        dataIndex: ['isActive'],
        width: 80,
        align: 'center',
        render: (isActive, record) => (
          <Switch
            size="small"
            checked={isActive}
            onChange={checked => handleToggleActive(record, checked)}
            disabled={loading}
          />
        ),
      },
      {
        title: translateX('actions'),
        dataIndex: 'actions',
        width: 90,
        align: 'center',
        render: (_, record) => (
          <Space size="small">
            {record.sourceType === 'meta' ? (
              <Tooltip title={translateX('edit')}>
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(record)}
                  disabled={loading}
                />
              </Tooltip>
            ) : (
              <Tooltip title={translateX('reconnect')}>
                <Button
                  type="text"
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={() => handleReconnect(record)}
                  disabled={loading || record.connectionStatus === 'connected'}
                />
              </Tooltip>
            )}
            <Popconfirm
              title={translateX('confirm_delete')}
              onConfirm={() => handleDelete(record)}
              okText={translateX('yes')}
              cancelText={translateX('no')}
            >
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                disabled={loading}
              />
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [loading, handleEdit, handleDelete, handleToggleActive, handleReconnect] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Flex vertical gap={12}>
      <Table
        rowKey="_id"
        columns={columns}
        dataSource={numbers}
        size="small"
        loading={loading}
        locale={{ emptyText: translateX('no_data') }}
        pagination={numbers.length > 10 ? { pageSize: 10, size: 'small' } : false}
      />

      <Flex justify="flex-end">
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} disabled={loading}>
          {translateX('add_phone')}
        </Button>
      </Flex>

      <AddWhatsappModal
        show={showAddModal}
        setShow={setShowAddModal}
        editingRecord={editingRecord}
        onSuccess={handleSaveSuccess}
        afterClose={handleModalClose}
      />

      <EditMetaPhoneModal
        show={showEditMetaModal}
        setShow={setShowEditMetaModal}
        editingRecord={editingRecord}
        onSuccess={handleSaveSuccess}
        afterClose={handleModalClose}
      />

      <ReconnectBaileysModal
        show={showReconnectModal}
        setShow={setShowReconnectModal}
        record={reconnectRecord}
        onSuccess={handleReconnectSuccess}
        afterClose={handleReconnectAfterClose}
      />
    </Flex>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default WhatsAppTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************

function formatPhone(phone) {
  if (!phone) return null;
  const digits = String(phone).trim().replace(/^\+/, '');
  if (digits.startsWith('55') && digits.length >= 12) {
    return parsePhoneToDisplay(`+${digits}`, undefined, { showCountryCode: true }) || `+${digits}`;
  }
  return parsePhoneToDisplay(digits, 'BR', { showCountryCode: true }) || `+${digits}`;
}

/**
 * Renders a colored Tag reflecting the real-time Baileys connection state.
 * Statuses emitted by the backend: 'connected', 'disconnected', 'connecting', 'qrcode'.
 */
function ConnectionStatusTag({ status }) {
  const config = {
    connected: { color: 'success', label: translateX('connected') },
    disconnected: { color: 'error', label: translateX('disconnected') },
    connecting: { color: 'processing', label: translateX('connecting') },
    qrcode: { color: 'warning', label: translateX('qrcode') },
  };
  const { color, label } = config[status] || { color: 'default', label: status || '—' };
  return <Tag color={color}>{label}</Tag>;
}
